import { useRef, useCallback, useState, useEffect } from 'react';
import { Howl } from 'howler';

interface UseBotAudioOptions {
  isEnabled: boolean;
}

interface UseBotAudioReturn {
  play: (base64Audio: string) => void;
  stop: () => void;
  isSpeaking: boolean;
  hasPendingAudio: boolean;
}

/**
 * Hook TTS - Bot audio playback
 * Sử dụng Howler.js để phát audio base64 dạng stream chunks từ backend nối đuôi nhau.
 */
export function useBotAudio(options: UseBotAudioOptions): UseBotAudioReturn {
  const { isEnabled } = options;

  const soundRef = useRef<Howl | null>(null);
  const isEnabledRef = useRef<boolean>(isEnabled);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Hàng đợi lưu các chunk base64 audio cần phát tiếp theo
  const audioQueueRef = useRef<string[]>([]);
  const prevEnabledRef = useRef<boolean>(isEnabled);
  // Track xem audio player có đang chạy queue hay không
  const isPlayingRef = useRef<boolean>(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (soundRef.current) {
        soundRef.current.stop();
        soundRef.current.unload();
        soundRef.current = null;
      }
    };
  }, []);

  // Handle mute/unmute transitions
  useEffect(() => {
    const wasPreviouslyEnabled = prevEnabledRef.current;
    prevEnabledRef.current = isEnabled;
    isEnabledRef.current = isEnabled;

    if (wasPreviouslyEnabled && !isEnabled) {
      // Mute: Chỉ giảm volume về 0, giữ nguyên hàng đệm phát ngầm
      console.log('[TTS] Muting - setting volume to 0');
      if (soundRef.current) {
        soundRef.current.volume(0);
      }
      setIsSpeaking(false);
    } else if (!wasPreviouslyEnabled && isEnabled) {
      // Unmute: Tăng volume lên 1.0
      console.log('[TTS] Unmuting - setting volume to 1.0');
      if (soundRef.current) {
        soundRef.current.volume(1.0);
      }
      if (isPlayingRef.current) {
        setIsSpeaking(true);
      }
    }
  }, [isEnabled]);

  // Hàm phát tiếp chunk trong hàng đợi
  const playNext = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      console.log('[TTS] Queue empty, playback finished');
      isPlayingRef.current = false;
      setIsSpeaking(false);
      soundRef.current = null;
      return;
    }

    const nextChunk = audioQueueRef.current[0];
    if (!nextChunk) {
      audioQueueRef.current.shift();
      playNext();
      return;
    }

    console.log('[TTS] Playing next chunk from queue, remaining in queue:', audioQueueRef.current.length - 1);

    const sound = new Howl({
      src: [`data:audio/mpeg;base64,${nextChunk}`],
      format: ['mp3'],
      html5: false,
      volume: isEnabledRef.current ? 1.0 : 0, // Phát ở volume 0 nếu đang bị mute
      preload: true,
      onplay: () => {
        isPlayingRef.current = true;
        if (isEnabledRef.current) {
          setIsSpeaking(true);
        }
      },
      onend: () => {
        console.log('[TTS] Chunk ended naturally');
        audioQueueRef.current.shift();
        playNext();
      },
      onstop: () => {
        console.log('[TTS] Chunk stopped');
        isPlayingRef.current = false;
        setIsSpeaking(false);
      },
      onloaderror: (_id, error) => {
        console.error('[TTS] Load error on chunk:', error);
        audioQueueRef.current.shift();
        playNext();
      },
      onplayerror: (_id, error) => {
        console.error('[TTS] Play error on chunk, unlocking and retrying...', error);
        sound.once('unlock', () => {
          sound.play();
        });
      },
    });

    soundRef.current = sound;
    sound.play();
  }, []);

  const play = useCallback((base64AudioChunk: string) => {
    if (!base64AudioChunk) {
      console.log('[TTS] Không có audio chunk data');
      return;
    }

    console.log('[TTS] Audio chunk received. Enabled:', isEnabledRef.current, 'Queue size:', audioQueueRef.current.length);
    audioQueueRef.current.push(base64AudioChunk);

    if (!isPlayingRef.current) {
      isPlayingRef.current = true;
      playNext();
    }
  }, [playNext]);

  const stop = useCallback(() => {
    if (soundRef.current) {
      soundRef.current.stop();
      soundRef.current.unload();
      soundRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsSpeaking(false);
    console.log('[TTS] Audio interrupted & queue cleared');
  }, []);

  return {
    play,
    stop,
    isSpeaking,
    hasPendingAudio: false,
  };
}

export default useBotAudio;
